import type { ComponentClass } from '../Component';
import type { Entity } from '../Entity';
import { UIDs } from '../Entity';
import type { Scene } from '../Scene';
import { ID32 } from './ID32';
import type {
  NotQueryComponent,
  OrQueryComponent,
  QueryComponent,
  QueryKey,
} from './Query';
import { Query } from './Query';

export type QueryReacts = 'added' | 'removed' | 'modified';
export type QueriesObject = {
  [x: string]: {
    components: QueryComponent[];
    added?: boolean;
    removed?: boolean;
    modified?: boolean;
  };
};

export type QueryResults = {
  [x: string]: Query;
};

export class QueryManager {
  private readonly _scene: Scene;
  private readonly _queries: { [x: string]: Query };

  constructor(world: Scene) {
    this._scene = world;

    // Queries indexed by a unique identifier for the components it has
    this._queries = {};
  }

  /** Destroy all queries */
  public destroy() {
    for (const queryName in this._queries) {
      this._queries[queryName].destroy();
    }
  }

  /**
   * Adds/removes an entity from a query after a component has been added
   * @param entity - entity to be added/removed
   */
  public onEntityComponentAdded(entity: Entity): void {
    // Check each indexed query to see if we need to add this entity to the list
    for (const queryName in this._queries) {
      const query = this._queries[queryName];

      query.match(entity.componentBitHash)
        ? query.addEntity(entity)
        : query.removeEntity(entity);
    }
  }

  /**
   * Adds/removes an entity from a query after a component has been removed
   * @param entity - entity to be added/removed
   */
  public onEntityComponentRemoved(entity: Entity): void {
    for (const queryName in this._queries) {
      const query = this._queries[queryName];

      query.match(entity.componentBitHash)
        ? query.addEntity(entity)
        : query.removeEntity(entity);
    }
  }

  /**
   * Adds an entity to any matching queries
   * @param entity - entity to be added
   */
  public onEntityAdded(entity: Entity): void {
    this.onEntityComponentAdded(entity);
  }

  /**
   * Removed an entity from any matching queries
   * @param entity - entity to be removed
   */
  public onEntityRemoved(entity: Entity): void {
    for (const queryName in this._queries) {
      this._queries[queryName].removeEntity(entity);
    }
  }

  /**
   * Returns a query based off of the components provided
   * @param components - components to check
   * @param name - name of the query
   */
  public getQuery(components: QueryComponent[], name: string): Query {
    if (components.length === 0) {
      throw Error('[QueryManager] cannot create empty query');
    }

    const key = this.generateQueryKey(components);
    let query = this._queries[key.name];

    if (!query) {
      this._queries[key.name] = query = new Query(this._scene, key, name);
    }

    return query;
  }

  /**
   * Generate a query key using component ids
   * @param components - components to generate key
   */
  public generateQueryKey(components: QueryComponent[]): QueryKey {
    const componentID = new ID32();
    const notComponentID = new ID32();
    const orComponentIDs: ID32[] = [];
    let orNames = '';

    const setID = (component: ComponentClass) => {
      if (component.ID === undefined) {
        component.ID = UIDs._COMPONENT++;
      }
    };

    components.forEach((component) => {
      if (typeof component === 'object') {
        if (component.operator === 'not') {
          component = (component as NotQueryComponent).component;

          setID(component);
          notComponentID.setIndex(component.ID!);
        } else {
          const orId = new ID32();

          (component as OrQueryComponent).components.forEach((component) => {
            setID(component);
            orId.setIndex(component.ID!);
          });
          orComponentIDs.push(orId);
          orNames += `${orId.id.toString()}||`;
        }
      } else {
        setID(component);
        componentID.setIndex(component.ID!);
      }
    });

    return {
      name: `${componentID.id.toString()}!${notComponentID.id.toString()}||${orNames}`,
      componentID,
      notComponentID,
      orComponentIDs,
    };
  }
}
